import CheckIcon from "@mui/icons-material/Check";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { Box, IconButton, Tooltip, useTheme } from "@mui/material";
import { SystemStyleObject, Theme } from "@mui/system";
import React, { useEffect, useState } from "react";
import CustomTypography from "./form/CustomTypography";

const skuCopyRowRootSx: SystemStyleObject<Theme> = {
  display: "inline-flex",
  gap: 0.5,
  minWidth: 0,
  maxWidth: "100%",
};

const skuCopyRowClampSx: SystemStyleObject<Theme> = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  lineHeight: 1.35,
  minWidth: 0,
};

const skuCopyRowWrapSx: SystemStyleObject<Theme> = {
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  minWidth: 0,
};

const skuCopyRowEllipsisSx: SystemStyleObject<Theme> = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export interface SkuCopyRowProps {
  sku: string | null | undefined;
  emptyFallback?: string;
  suffix?: string;
  /** Tooltip / aria label for the copy action. Default: "Copy SKU" */
  copyLabel?: string;
  className?: string;
  fontWeight?: number;
  color?: string;
  variant?: "body2" | "body3" | "heading6" | "heading7";
  /** When true, text wraps fully (no 2-line clamp / ellipsis). */
  allowWrap?: boolean;
  /** Extra styles merged after the component's own text CSS. */
  sx?: SystemStyleObject<Theme>;
  /** @deprecated Prefer `sx`. Kept for existing callers. */
  typographySx?: SystemStyleObject<Theme>;
}

/**
 * Displays a value with a one-click copy button.
 * Self-contained — no parent copy state required.
 * Long values wrap up to 2 lines, then ellipsis (unless allowWrap).
 */
const SkuCopyRow: React.FC<SkuCopyRowProps> = ({
  sku,
  emptyFallback = "No SKU",
  suffix,
  copyLabel = "Copy SKU",
  className,
  fontWeight,
  color,
  variant = "body2",
  allowWrap = false,
  sx,
  typographySx,
}) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const value = sku?.trim() || "";
  const [copied, setCopied] = useState(false);
  const isLongSku = !allowWrap && value.length > 20;
  const textColor = color ?? colors.greyTextColor;

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // ignore clipboard errors
    }
  };

  const ownTextSx = isLongSku
    ? skuCopyRowClampSx
    : allowWrap
      ? skuCopyRowWrapSx
      : skuCopyRowEllipsisSx;

  const mergedTextSx: SystemStyleObject<Theme> = {
    ...ownTextSx,
    ...typographySx,
    ...sx,
  };

  if (!value) {
    return (
      <CustomTypography
        variant={variant}
        color={colors.greyTextColor}
        sx={mergedTextSx}
      >
        {emptyFallback}
        {suffix || ""}
      </CustomTypography>
    );
  }

  const label = `${value}${suffix || ""}`;

  return (
    <Box
      sx={{
        ...skuCopyRowRootSx,
        alignItems: isLongSku || allowWrap ? "flex-start" : "center",
      }}
    >
      <Tooltip title={isLongSku || allowWrap ? value : ""}>
        <CustomTypography
          className={className}
          variant={variant}
          fontWeight={fontWeight}
          color={textColor}
          sx={mergedTextSx}
        >
          {label}
        </CustomTypography>
      </Tooltip>
      <Tooltip title={copied ? "Copied" : copyLabel}>
        <IconButton
          size="small"
          onClick={(e) => {
            void handleCopy(e);
          }}
          aria-label={`${copyLabel} ${value}`}
          sx={{
            p: 0.3,
            flexShrink: 0,
            mt: isLongSku || allowWrap ? 0.1 : 0,
            color: copied ? colors.purpleMain : colors.greyTextColor,
          }}
        >
          {copied ? (
            <CheckIcon sx={{ fontSize: 12 }} />
          ) : (
            <ContentCopyOutlinedIcon sx={{ fontSize: 12 }} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default SkuCopyRow;
